// src/pages/CatalogoPage.jsx

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, Button, CircularProgress, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  MenuItem, FormControl, InputLabel, Select, Accordion, AccordionSummary,
  AccordionDetails, Divider, alpha, useMediaQuery, Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

// Lista actualizada de categorías que coincide con el backend
const categoriasDisponibles = [
  'Hematología',
  'Química Sanguínea',
  'Electrolitos',
  'Inmunología',
  'Marcadores Tumorales',
  'Hormonas',
  'Drogas en sangre',
  'Química Urinaria',
  'Pruebas de Coagulación',
  'Uroanálisis',
  'Coprología',
  'Microbiología',
  'Drogas de Abuso en orina',
  'Inmunoglobulinas',
  'Marcadores Cardiacos',
  'Pruebas Especiales',
  'Otros'  // Solo usamos "Otros" (plural)
];

const CatalogoPage = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { user } = useAuth(); // Obtener el usuario actual
  const [examenes, setExamenes] = useState([]);
  const [examenesAgrupados, setExamenesAgrupados] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [currentExamen, setCurrentExamen] = useState({
    id_examen: null,
    nombre: '',
    categoria: 'Otros',
    descripcion: ''
  });

  const fetchExamenes = () => {
    setLoading(true);

    // Obtener el ID del médico actual
    const medicoId = user?.medico?.id_medico;

    // Si no hay médico identificado, mostrar mensaje de error
    if (!medicoId) {
      toast.error('No se pudo identificar al médico actual');
      setLoading(false);
      return;
    }

    // Añadir filtro por médico a la URL o en los parámetros de consulta
    axiosInstance.get('/catalogo-examenes/', {
      params: { medico: medicoId } // Filtrar por médico
    })
      .then(response => {
        // Normalizar las categorías (convertir "Otro" a "Otros")
        const examenesNormalizados = response.data.map(examen => ({
          ...examen,
          categoria: examen.categoria === 'Otro' ? 'Otros' : examen.categoria
        }));

        setExamenes(examenesNormalizados);

        // Agrupar exámenes por categoría
        const agrupados = {};
        examenesNormalizados.forEach(examen => {
          const categoria = examen.categoria === 'Otro' ? 'Otros' : examen.categoria;
          if (!agrupados[categoria]) {
            agrupados[categoria] = [];
          }
          agrupados[categoria].push(examen);
        });
        setExamenesAgrupados(agrupados);
      })
      .catch((error) => {
        console.error("Error al cargar el catálogo:", error);
        toast.error('No se pudo cargar el catálogo.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.medico?.id_medico) {
      fetchExamenes();
    }
  }, [user]); // Dependencia en user para recargar cuando cambie el usuario

  const handleOpen = (examen = null, mode = 'edit') => {
    if (examen) {
      if (mode === 'view') {
        setViewMode(true);
        setIsEditMode(false);
      } else {
        setViewMode(false);
        setIsEditMode(true);
      }

      setCurrentExamen({
        id_examen: examen.id_examen,
        nombre: examen.nombre,
        categoria: examen.categoria === 'Otro' ? 'Otros' : examen.categoria,
        descripcion: examen.descripcion || '',
        es_predeterminado: examen.es_predeterminado
      });
    } else {
      setViewMode(false);
      setIsEditMode(false);
      setCurrentExamen({ id_examen: null, nombre: '', categoria: 'Otros', descripcion: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setViewMode(false);
  };

  const handleSave = () => {
    const method = isEditMode ? 'put' : 'post';
    const url = isEditMode ? `/catalogo-examenes/${currentExamen.id_examen}/` : '/catalogo-examenes/';

    const payload = {
      nombre: currentExamen.nombre,
      categoria: currentExamen.categoria,
      descripcion: currentExamen.descripcion
    };

    axiosInstance[method](url, payload)
      .then(() => {
        toast.success(`Exámen ${isEditMode ? 'actualizado' : 'creado'} con éxito.`);
        fetchExamenes();
        handleClose();
      })
      .catch((error) => {
        console.error("Error al guardar:", error.response?.data);

        // Mostrar mensajes de error específicos
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.categoria) {
            toast.error(`Error en categoría: ${errorData.categoria[0]}`);
          } else if (errorData.nombre) {
            toast.error(`Error en nombre: ${errorData.nombre[0]}`);
          } else {
            toast.error('Hubo un error al guardar el exámen.');
          }
        } else {
          toast.error('Hubo un error al guardar el exámen.');
        }
      });
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este exámen?')) {
      axiosInstance.delete(`/catalogo-examenes/${id}/`)
        .then(() => {
          toast.success('Exámen eliminado.');
          fetchExamenes();
        })
        .catch((error) => {
          console.error("Error al eliminar:", error);
          toast.error('No se pudo eliminar el exámen.');
        });
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
  }

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Box
        display="flex"
        justifyContent={isSmDown ? 'center' : 'space-between'}
        alignItems={isSmDown ? 'stretch' : 'center'}
        flexDirection={isSmDown ? 'column' : 'row'}
        gap={2}
        mb={4}
      >
        <Box display="flex" alignItems="center" justifyContent={isSmDown ? 'center' : 'flex-start'}>
          <ScienceIcon
            sx={{
              fontSize: isSmDown ? 36 : 50,
              width: isSmDown ? 42 : 56,
              height: isSmDown ? 42 : 56,
              mr: 2,
              color: 'primary.main',
              padding: 1,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }}
          />
          <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 'bold' }}>
            Catálogo de Exámenes de Laboratorio
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => handleOpen()}
          size={isSmDown ? "medium" : "large"}
          fullWidth={isSmDown}
          sx={{
            borderRadius: '20px',
            boxShadow: 'none',
            padding: isSmDown ? '10px 16px' : '10px 24px',
            '&:hover': {
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1),0px 4px 5px 0px rgba(0,0,0,0.07)',
            }
          }}
          aria-label="Añadir nuevo exámen"
        >
          {isSmDown ? 'Añadir' : 'Añadir Nuevo Exámen'}
        </Button>
      </Box>

      {/* Exámenes agrupados por categoría */}
      {Object.keys(examenesAgrupados).length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: '16px',
            boxShadow: theme.shadows[2]
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No hay exámenes disponibles. Comienza añadiendo uno nuevo.
          </Typography>
        </Paper>
      ) : (
        Object.keys(examenesAgrupados).sort().map(categoria => (
          <Accordion
            key={categoria}
            defaultExpanded={!isMdDown} // En desktop expandido por defecto; en móvil, contraído
            sx={{
              mb: 2,
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: theme.shadows[2],
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                margin: '0 0 16px 0',
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'primary.contrastText' }} />}
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                py: isSmDown ? 1 : 1.25,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.9)
                },
                '&.Mui-expanded': {
                  minHeight: isSmDown ? 48 : 56,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
                '& .MuiAccordionSummary-content': {
                  my: 0
                }
              }}
            >
              <Typography
                variant={isSmDown ? "subtitle1" : "h6"}
                sx={{ fontWeight: 'medium', textTransform: 'uppercase' }}
              >
                {categoria} ({examenesAgrupados[categoria].length})
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
              {/* Vista responsive: Cards en móvil, Tabla en desktop */}
              {isMdDown ? (
                <Stack spacing={1.25} p={2}>
                  {examenesAgrupados[categoria].map((examen) => (
                    <Paper
                      key={examen.id_examen}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.divider, 0.6)
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textTransform: 'uppercase', fontWeight: 600, flex: 1, minWidth: 0 }}
                          noWrap
                          title={examen.nombre}
                        >
                          {examen.nombre}
                        </Typography>
                        {examen.es_predeterminado ? (
                          <Chip
                            label="Predeterminado"
                            color="info"
                            size="small"
                            sx={{ borderRadius: '16px', fontWeight: 500, flexShrink: 0 }}
                          />
                        ) : (
                          <Chip
                            label="Personal"
                            color="success"
                            size="small"
                            sx={{ borderRadius: '16px', fontWeight: 500, flexShrink: 0 }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.75,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {examen.descripcion && examen.descripcion.trim().length > 0
                          ? examen.descripcion
                          : 'SIN DESCRIPCIÓN'}
                      </Typography>

                      <Box mt={1} display="flex" gap={0.5} justifyContent="flex-end">
                        {!examen.es_predeterminado ? (
                          <>
                            <IconButton
                              onClick={() => handleOpen(examen, 'edit')}
                              color="primary"
                              size="medium"
                              aria-label="Editar exámen"
                              sx={{
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(examen.id_examen)}
                              color="error"
                              size="medium"
                              aria-label="Eliminar exámen"
                              sx={{
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1)
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => handleOpen(examen, 'view')}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              borderRadius: '20px',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                              }
                            }}
                            aria-label="Ver detalles del exámen"
                          >
                            Ver
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table sx={{ tableLayout: 'fixed' }} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="30%" sx={{ fontWeight: 'bold', fontSize: '1.05rem', backgroundColor: theme.palette.background.default }}>
                          NOMBRE DEL EXAMEN
                        </TableCell>
                        <TableCell width="15%" sx={{ fontWeight: 'bold', fontSize: '1.05rem', backgroundColor: theme.palette.background.default }}>
                          TIPO
                        </TableCell>
                        <TableCell width="40%" sx={{ fontWeight: 'bold', fontSize: '1.05rem', backgroundColor: theme.palette.background.default }}>
                          DESCRIPCIÓN
                        </TableCell>
                        <TableCell width="15%" align="center" sx={{ fontWeight: 'bold', fontSize: '1.05rem', backgroundColor: theme.palette.background.default }}>
                          ACCIONES
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {examenesAgrupados[categoria].map((examen) => (
                        <TableRow key={examen.id_examen} hover>
                          <TableCell width="30%" sx={{ textTransform: 'uppercase', fontSize: '1rem' }}>{examen.nombre}</TableCell>
                          <TableCell width="15%">
                            {examen.es_predeterminado ? (
                              <Chip
                                label="Predeterminado"
                                color="info"
                                size="small"
                                sx={{ borderRadius: '16px', fontWeight: 500 }}
                              />
                            ) : (
                              <Chip
                                label="Personal"
                                color="success"
                                size="small"
                                sx={{ borderRadius: '16px', fontWeight: 500 }}
                              />
                            )}
                          </TableCell>
                          <TableCell width="40%" sx={{ fontSize: '1rem' }}>
                            {examen.descripcion ? (
                              examen.descripcion.length > 50 ?
                                `${examen.descripcion.substring(0, 50)}...` :
                                examen.descripcion
                            ) : (
                              <Typography variant="body2" color="textSecondary" fontStyle="italic">
                                SIN DESCRIPCIÓN
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell width="15%" align="center">
                            {!examen.es_predeterminado ? (
                              <Box>
                                <IconButton
                                  onClick={() => handleOpen(examen, 'edit')}
                                  color="primary"
                                  size="small"
                                  aria-label="Editar exámen"
                                  sx={{
                                    mr: 1,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                    }
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDelete(examen.id_examen)}
                                  color="error"
                                  size="small"
                                  aria-label="Eliminar exámen"
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.error.main, 0.1)
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => handleOpen(examen, 'view')}
                                startIcon={<VisibilityIcon />}
                                sx={{
                                  borderRadius: '20px',
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                  }
                                }}
                              >
                                Ver Detalles
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: isSmDown ? 0 : '28px',
            padding: isSmDown ? 0 : '8px',
            boxShadow: theme.shadows[5]
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: isSmDown ? 0 : 1,
            pt: isSmDown ? 1 : 2,
            px: isSmDown ? 2 : 3,
            fontSize: isSmDown ? '1.25rem' : '1.5rem',
            fontWeight: 600
          }}
        >
          {viewMode
            ? 'Detalles del Exámen'
            : isEditMode
              ? 'Editar Exámen'
              : 'Añadir Nuevo Exámen'}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'text.secondary'
            }}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: isSmDown ? 2 : 3, py: isSmDown ? 1 : 2 }}>
          <TextField
            autoFocus={!viewMode && !isSmDown}
            margin="dense"
            label="Nombre del Exámen"
            fullWidth
            variant="outlined"
            value={currentExamen.nombre}
            onChange={(e) => !viewMode && setCurrentExamen({ ...currentExamen, nombre: e.target.value })}
            sx={{
              mb: 2,
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                }
              }
            }}
            required
            InputProps={{
              readOnly: viewMode
            }}
          />

          <FormControl
            fullWidth
            margin="dense"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                }
              }
            }}
            required
          >
            <InputLabel>Categoría</InputLabel>
            <Select
              value={currentExamen.categoria}
              label="Categoría"
              onChange={(e) => !viewMode && setCurrentExamen({ ...currentExamen, categoria: e.target.value })}
              inputProps={{ readOnly: viewMode }}
            >
              {categoriasDisponibles.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Descripción (Opcional)"
            fullWidth
            multiline
            rows={isSmDown ? 3 : 4}
            variant="outlined"
            value={currentExamen.descripcion}
            onChange={(e) => !viewMode && setCurrentExamen({ ...currentExamen, descripcion: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                }
              }
            }}
            InputProps={{
              readOnly: viewMode
            }}
          />

          {viewMode && currentExamen.es_predeterminado && (
            <Box
              mt={2}
              p={2}
              bgcolor={alpha(theme.palette.info.main, 0.1)}
              borderRadius={2}
            >
              <Typography variant="body2" color="info.dark">
                Este es un examen predeterminado del sistema y no puede ser modificado.
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* Acciones: apiladas y sticky en móviles */}
        {isSmDown ? (
          <DialogActions
            sx={{
              px: 2,
              py: 2,
              position: 'sticky',
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              zIndex: 1,
              m: 0
            }}
          >
            <Stack direction="column" spacing={1} width="100%">
              <Button
                onClick={handleClose}
                variant={viewMode ? "outlined" : "tonal"}
                fullWidth
                sx={{
                  borderRadius: '20px',
                  ...(viewMode ? {} : {
                    backgroundColor: theme.palette.mode === 'light' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.2),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.3),
                    }
                  })
                }}
              >
                {viewMode ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!viewMode && (
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={!currentExamen.nombre || !currentExamen.categoria}
                  fullWidth
                  sx={{
                    borderRadius: '20px',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1),0px 4px 5px 0px rgba(0,0,0,0.07)',
                    }
                  }}
                >
                  {isEditMode ? 'Guardar Cambios' : 'Crear'}
                </Button>
              )}
            </Stack>
          </DialogActions>
        ) : (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleClose}
              variant={viewMode ? "outlined" : "tonal"}
              sx={{
                borderRadius: '20px',
                ...(viewMode ? {} : {
                  backgroundColor: theme.palette.mode === 'light' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.3),
                  }
                })
              }}
            >
              {viewMode ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!viewMode && (
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={!currentExamen.nombre || !currentExamen.categoria}
                sx={{
                  borderRadius: '20px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1),0px 4px 5px 0px rgba(0,0,0,0.07)',
                  }
                }}
              >
                {isEditMode ? 'Guardar Cambios' : 'Crear'}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default CatalogoPage;
